import { Box, Button, Center, LoadingOverlay, Text, Drawer, Title, ActionIcon, CloseIcon } from "@mantine/core";
import { FC, Fragment, useEffect, useState } from "react";
import GeoCard from "./GeoCard";
import CheckinPin from "components/CheckinPin";
import useExternalApiService from "helpers/ExternalApiService";

const apiUrl = process.env.REACT_APP_EAI_URL;

const ValidGeoCard: FC<{
  selected: boolean;
  onClick: () => void;
  formattedValidAddress: string;
  latLon?: [number, number];
}> = ({
  selected,
  latLon,
  onClick,
  formattedValidAddress
}) => (
    <GeoCard
      i="valid"
      selected={selected}
      latLon={latLon}>
      <Box
        display="flex"
        onClick={() => onClick()}>
        <CheckinPin size={15} p={3} />
        <Box>
          <Title order={4} fw="normal" ml="xs">
            Recommended Address
          </Title>
          <Text c="#4CAF50" size="xs" display="inline" ml={10}>
          GEO-VERIFIED: CAN DO VERIFY CHECK-IN'S
          </Text>
          <Text mt="md" ml="xs" c="dimmed">
            {formattedValidAddress}
          </Text>
        </Box>
      </Box>
      <Box 
        display="flex" 
        flex={{ alignItems: "center" }}
        ml = "xl"
        mt="md"        
      >
      </Box>
    </GeoCard>
  );

const InvalidGeoCard: FC<{
  selected: boolean;
  onClick: () => void;
  formattedCurrentAddress: string;
}> = ({
  selected,
  onClick,
  formattedCurrentAddress
}) => (
    <GeoCard
      i="invalid"
      selected={selected}
      selectedColor="#e64b37">
      <Box
        display="flex"
        onClick={() => onClick()}>
        <ActionIcon radius="50%" p={2} size={15} color="#e64b37">
          <CloseIcon size="20px" />
        </ActionIcon>
        <Box>
          <Title order={4} fw="normal" ml="xs">
            What you Entered
          </Title>
          <Text c="red" size="xs" display="inline" ml={10}>
            NO LAT & LONG: CAN'T DO VERIFY CHECK-IN'S
          </Text>
          <Text mt="md" ml="xs" c="dimmed">
            {formattedCurrentAddress}
          </Text>
        </Box>
      </Box>      
    </GeoCard>
  );

interface AddressValidation {
  locality: string;
  administrativeArea: string;
  postalCode: string;
  addressLines: string[];
}

interface ValidatedAddress extends AddressValidation {
  latLon: [number, number];
}

type Verdict = "valid" | "invalid" | "replaced";

interface Props {
  opened: boolean;
  onClose: () => void;
  addressToValidate: AddressValidation;
  onConfirm: (address?: ValidatedAddress) => void;
}

const GeoValidationDrawer: FC<Props> = ({
  addressToValidate,
  onConfirm,
  opened,
  onClose
}) => {
  const [useValidatedAddress, setUseValidatedAddress] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [validAddress, setValidAddress] = useState<ValidatedAddress>();
  const [verdict, setVerdict] = useState<Verdict>();
  const { postData} = useExternalApiService(apiUrl);

  const formatedValidAddress = `${validAddress?.addressLines[0]}
    \n${validAddress?.addressLines[1]}
    \n${validAddress?.locality}, ${validAddress?.administrativeArea} ${validAddress?.postalCode}`;

  const formatedCurrentAddress = `${addressToValidate?.addressLines[0]}
    \n${addressToValidate?.addressLines[1]}
    \n${addressToValidate?.locality}, ${addressToValidate?.administrativeArea} ${addressToValidate?.postalCode}`;

  useEffect(() => {
    if (opened) {
      setIsValidating(true);
      postData("/api/geo_validate", addressToValidate)
        .then((r) => {
          if (r?.validAddress) {
            setValidAddress({
              ...addressToValidate,
              latLon: r.validAddress.latLon
            });
            setVerdict("valid");
            setUseValidatedAddress(true);
          }

          if (r?.invalidAddress) {
            setVerdict("invalid");
            setUseValidatedAddress(false);
          }

          if (r?.replacements) {
            const addressInfo = r?.replacements?.postalAddress;
            const addressLines = addressInfo?.addressLines?.length === 2 ? addressInfo?.addressLines
              : (addressInfo?.addressLines?.length === 1 ? [addressInfo?.addressLines[0], ""] : ["", ""]);

            setValidAddress({
              postalCode: addressInfo?.postalCode || addressToValidate.postalCode,
              locality: addressInfo?.locality || addressToValidate.locality,
              administrativeArea: addressInfo?.administrativeArea || addressToValidate.administrativeArea,
              addressLines: addressLines,
              latLon: r?.replacements?.latLon,
            });
            setVerdict("replaced");
          }
        })
        .catch((err) => console.log(err))
        .finally(() => setIsValidating(false));
    }
  }, [opened]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      size="xl"
      radius="20px 20px 0 0"
      title={(
        <Title order={2} fw="normal">
          Validate your Address
        </Title>
      )}>
      <LoadingOverlay visible={isValidating} />
      {verdict === "valid" && (
        <Fragment>
          <ValidGeoCard 
            selected={useValidatedAddress}
            onClick={() => setUseValidatedAddress(true)}
            formattedValidAddress={formatedValidAddress}
            latLon={validAddress?.latLon} />
          <Center mt="lg">
            <Button onClick={() => onConfirm(validAddress)}>
              CONFIRM ADDRESS
            </Button>
          </Center>
        </Fragment>
      )}

      {verdict === "invalid" && (
        <Fragment>
          <InvalidGeoCard
            selected={!useValidatedAddress}
            onClick={() => setUseValidatedAddress(false)}
            formattedCurrentAddress={formatedCurrentAddress} />
          <Center mt="lg">
            <Button onClick={() => onConfirm()}>
              PROCEED WITHOUT VALIDATION
            </Button>
          </Center>
        </Fragment>
      )}

      {verdict === "replaced" && (
        <Fragment>
          <InvalidGeoCard
            selected={!useValidatedAddress}
            onClick={() => setUseValidatedAddress(false)}
            formattedCurrentAddress={formatedCurrentAddress} />
          <ValidGeoCard 
            selected={useValidatedAddress}
            onClick={() => setUseValidatedAddress(true)}
            formattedValidAddress={formatedValidAddress}
            latLon={validAddress?.latLon} />

          <Center mt="lg">
            <Button onClick={() => onConfirm(useValidatedAddress ? validAddress : undefined)}>
              SELECT ADDRESS
            </Button>
          </Center>
        </Fragment>
      )}
    </Drawer>
  );
};

export default GeoValidationDrawer;